import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trophy, Fire } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";

const fmt = (n) => (n || 0).toLocaleString();

export default function DanceChallenge() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/dance/${slug}`); setData(data); }
    catch (err) { console.error("challenge load failed", err); }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  if (loading) return <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/40">Loading…</div>;
  if (!data?.challenge) return <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/40">Challenge not found.</div>;

  const c = data.challenge;
  const entries = data.entries || [];

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="dance-challenge-detail">
      <div className="relative h-52">
        <img src={c.cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/10" />
        <button onClick={() => nav(-1)} data-testid="challenge-back" className="absolute top-5 left-4 w-9 h-9 rounded-full crystal-glass flex items-center justify-center text-cream z-10">
          <ArrowLeft size={18} />
        </button>
        <div className="absolute inset-x-0 bottom-0 p-5">
          {data.is_weekly && <span className="px-2.5 py-1 rounded-full bg-jam-gold text-ink text-[10px] font-black tracking-[0.15em] uppercase inline-flex items-center gap-1"><Trophy size={12} weight="fill" /> Weekly Competition</span>}
          <h1 className="text-cream text-3xl font-black tracking-tighter mt-2">{c.emoji} {c.title}</h1>
          <p className="text-cream/70 text-sm">{c.description}</p>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-cream/70 text-xs font-bold">{fmt(data.entries_count)} entries</span>
            <button onClick={() => nav(`/create?tag=${c.hashtag}`)} data-testid="challenge-enter" className="ml-auto px-5 py-2 rounded-full bg-papaya text-ink text-xs font-black tracking-[0.15em] uppercase">Enter · #{c.hashtag}</button>
          </div>
        </div>
      </div>

      <section className="px-4 mt-5">
        <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3 px-1">{data.is_weekly ? "This week's leaderboard" : "Top entries"}</p>
        {entries.length === 0 ? (
          <div className="text-center py-16 px-6">
            <p className="text-cream/50 text-sm">No entries yet.</p>
            <p className="text-cream/40 text-xs mt-1">Post with #{c.hashtag} to be the first.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="challenge-entries">
            {entries.map((e, i) => (
              <Link key={e.id} to={`/p/${e.id}`} data-testid={`challenge-entry-${e.id}`} className="group relative aspect-[9/14] rounded-2xl overflow-hidden bg-white/5">
                {e.media_type === "video" ? <video src={fileUrl(e.media_path)} className="w-full h-full object-cover" muted /> : <img src={fileUrl(e.media_path)} alt="" className="w-full h-full object-cover" />}
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-ink/70 text-cream text-[10px] font-black">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}</span>
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-ink to-transparent">
                  <p className="text-cream text-[11px] font-bold line-clamp-1">@{e.author?.username}</p>
                  <p className="text-papaya text-[10px] font-black flex items-center gap-1"><Fire size={10} weight="fill" /> {fmt(e.score)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
